import { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import SwiperFarmCardList from "./SwiperFarmCardList";
import SkeletonCard  from "../common/SkeletonCard"
import CarouselNavigation from "../common/CarouselNavigation";

import { findFarmsByProximity } from "../../api/search-service/searchService"
import { getCategoryById } from "../../api/farm-service/categoryService";

import useUserLocation from "../../hooks/useUserLocation";

import FarmCardStyle from "../../assets/stylesheets/FarmCard.module.css";

import logger from "../../utils/logger";
import slugify from 'slugify';

const log = logger.create("AnimalsCategoryCarousel");

const animalsCarouselContainerTitle = "Meet the animals nearby";
const animalsCarouselContainerSubtitle = "Make new furry friends at nearby farms";

/**
 * AnimalsCarousel component displays a carousel of animal farms based on user location.
 * It fetches farms by proximity and displays them in a swiper carousel.
 * @param {Object} props - The component props.
 * @param {number} props.categoryId - The ID of the category to filter farms by
 * @returns {JSX.Element} The rendered component.
 */
function AnimalsCarousel({categoryId}) {
  const navigate                      = useNavigate();
  const swiperRef                     = useRef(null);
  const prevRef                       = useRef(null);
  const nextRef                       = useRef(null);
  const { location, error }           = useUserLocation()
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd]             = useState(false);
  const [category, setCategory]       = useState(null);
  const [farms, setFarms]             = useState([]);
  const [loading, setLoading]         = useState(true); 

  const VERBOSE                       = false;
  const DEFAULT_PROXIMITY_DISTANCE    = 10000; // 10,000 km radius
  const LIMIT                         = 6;
  const SORT                          = 'distance';
  const ORDER                         = 'ASC';

  // Function to update navigation state based on swiper state
  const updateNavState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const ANIMALS_CATEGORY_ID = 2; // Example category ID for animals
  
  // Fetch category data if categoryId is provided
  useEffect(() => {
    if (!categoryId) return;
    setLoading(true);
    setCategory(null);
    getCategoryById(categoryId)
      .then(category => setCategory(category))
      .catch((err) => {
        log.error("Error fetching category:", err);
      })
      .finally(() => setLoading(false)); 
  }, [categoryId]);

  // DEBUG: useUserLocation (IP/GPS) 
  useEffect(() => {
    if (!location) {
      log.log('[AnimalsCarousel][Location] null', { error });
      return;
    }
    const source = location?.source ?? (location?.ip ? 'IP' : 'GPS');
    log.log(`[AnimalsCarousel][Location] from ${source}:`, location);
  }, [location, error]); 


  // Fetch farms by proximity when location is available
  useEffect(() => {
    if (!location) return

    setLoading(true);     
    findFarmsByProximity({
      verbose: VERBOSE,
      latitude:  location.lat, 
      longitude: location.lng,
      distance:  DEFAULT_PROXIMITY_DISTANCE,
      categoryId: categoryId, // Use the provided categoryId
      limit: LIMIT,
      sort: SORT,
      order: ORDER
    })
      .then(response => {
        const farmsArray = Array.isArray(response.items) ? response.items : [];
        log.log("🔍 proximity result Animals:", farmsArray);
        setFarms(farmsArray)      
      })
      .catch((err) => {
        log.error("Error fetching farms by proximity:", err);
      })
      .finally(() => setLoading(false)); 
  }, [location])

  // Update swiper navigation state on slide change
  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;
    const onChange = () => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    };
    swiper.on("slideChange", onChange);
    onChange();
    return () => swiper.off("slideChange", onChange);
  }, [farms]);

  return (
    <section className={FarmCardStyle["events-section"]}>
      <div className={FarmCardStyle["events-container"]}>
        <div className={FarmCardStyle["events-header"]}>
          <div>
            {loading
              ? <SkeletonCard variant="header" />
              : (
                <>
                  <h2 className={FarmCardStyle["events-title"]}>
                    {/* Animals Farms Near You &#62; */}
                    {/* {category?.data?.name ? `${category.data.name} Near You` : 'No category found'} &#62; */}
                    {animalsCarouselContainerTitle} &#62;
                  </h2>
                  <p className={FarmCardStyle["events-subtitle"]}>
                    {/* Visit nearby farms where you can meet, interact with, and learn about animals. */}
                    {/* {category?.data?.description ? ` ${category.data.description}` : ' No description available'} */}
                    {animalsCarouselContainerSubtitle}
                  </p>
                </>
              )
            }
          </div>
          {/* Carousel navigation buttons */}
          <CarouselNavigation 
            prevRef={prevRef}
            nextRef={nextRef}
            isBeginning={isBeginning}
            isEnd={isEnd}
          />
        </div>
        {/* Farm card swiper list */}
        <SwiperFarmCardList 
          farms={farms}
          loading={loading}
          onFarmClick={farm => {
            const slug = slugify(farm.name || '', { lower: true, strict: true });
            navigate(`/farm/${farm.id}/${slug}`);
          }}
          skeletonCount={LIMIT}
          prevRef={prevRef}
          nextRef={nextRef}
          updateNavState={updateNavState}
        />
      </div>
    </section>
  );
}

export default AnimalsCarousel;